import uniqBy from "lodash/uniqBy";

import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/supabase-types";
import { CADCurrencyConverter } from "./currency";
import { calculateBaseQuantity, itemUOMSelect, ItemUOMSelectResult } from "./item-uoms";
import { Item, itemSelect, ItemSelectResult, processItem } from "./items";
import { Job } from "../job-by-name";

export type Build = Omit<BuildSelectResult, "inputs" | "outputs"> & {
  inputs: BuildLineItem[];
  outputs: BuildLineItem[];
  linkedWOs: LinkedWorkOrderDetails[];
};

export interface BuildLineItem extends Omit<BuildLineItemSelectResult, "item"> {
  item: Item;
  /** Quantity for the line item converted to the base UOM */
  baseQuantity: number;
  linkedWO: LinkedWorkOrderDetails | undefined;
}

// TODO: Determine these types dynamically once TypeScript performance is good enough

// type BuildSelectResult = SupabaseSelectResult<"Build", typeof buildSelect, BuildSelectRelatedTables>;

// type BuildSelectRelatedTables =
//   | "BuildInputLineItem"
//   | "BuildOutputLineItem"
//   | "Job"
//   | "Workcenter"
//   | "Item"
//   | ItemSelectRelatedTables;

// type BuildInputLineItemSelectResult = BuildSelectResult["inputs"][0];
// type BuildOutputLineItemSelectResult = BuildSelectResult["outputs"][0];

interface BuildSelectResult extends Pick<Tables<"Build">, "id" | "number" | "date" | "comment" | "archived"> {
  job: Pick<Tables<"Job">, "id" | "name"> | null;
  workCenter: Pick<Tables<"Workcenter">, "id" | "name"> | null;
  inputs: BuildLineItemSelectResult[];
  outputs: BuildLineItemSelectResult[];
}

interface BuildLineItemSelectResult extends Pick<Tables<"BuildOutputLineItem">, "id" | "lineNumber" | "description"> {
  /** Notes are only included on build inputs */
  notes?: string | null;
  /** Work orders can only be linked to build outputs */
  linkedWorkOrderLineItem?: LinkedWorkOrderLineItem;
  /** Quantity for the line item in the selected ItemUOM */
  uomQuantity: number;
  item: ItemSelectResult;
  itemUOM: ItemUOMSelectResult | null;
  bin: Pick<Tables<"Bin">, "id" | "number"> | null;
  job: Pick<Tables<"Job">, "id" | "name"> | null;
}

export interface LinkedWorkOrderLineItem extends Pick<Tables<"WorkOrderLineItem">, "quantity"> {
  workOrder: LinkedWorkOrderDetails;
}

export type LinkedWorkOrderDetails = Pick<Tables<"WorkOrder">, "id" | "number">;

const builtLineItemSelect = `
  id,
  lineNumber,
  description,
  item:Item!inner(${itemSelect}),
  uomQuantity:quantity,
  itemUOM:ItemUOM(${itemUOMSelect}),
  bin:Bin(id, number),
  job:Job(id, name)
`;

const buildSelect = `
id,
number,
date,
job:Job(id, name),
workCenter:Workcenter(id, name),
comment,
archived,
inputs:BuildInputLineItem(
  ${builtLineItemSelect},
  notes
),
outputs:BuildOutputLineItem!inner(
  ${builtLineItemSelect},
  linkedWorkOrderLineItem:WorkOrderLineItem(
    quantity,
    workOrder:WorkOrder(id, number)
  )
)
`;

export async function getBuilds(
  { id: jobId, name: jobName }: Job,
  CADCurrencyConverter: CADCurrencyConverter,
): Promise<Build[]> {
  // Fetch non-deleted builds from SOS that have the job assigned on the build itself or the output line item
  const getBaseQuery = () =>
    supabase
      .from("Build")
      .select<typeof buildSelect, BuildSelectResult>(buildSelect)
      .eq("deleted", false)
      .eq("inputs.deleted", false)
      .eq("outputs.deleted", false)
      .order("date", { ascending: false })
      .order("lineNumber", { referencedTable: "inputs", ascending: true })
      .order("lineNumber", { referencedTable: "outputs", ascending: true })
      .throwOnError();
  const [buildsByJobId, buildsByOutputJobId] = await Promise.all([
    getBaseQuery()
      .eq("jobId", jobId)
      .then((x) => x.data ?? []),
    getBaseQuery()
      .eq("outputs.jobId", jobId)
      .then((x) => x.data ?? []),
  ]);
  const queryResult = uniqBy([...buildsByJobId, ...buildsByOutputJobId], (build) => build.id).sort((a, b) =>
    b.date.localeCompare(a.date),
  );

  // For each build, validate that all job assignments are either null, or match the job ID
  queryResult.forEach((build) => {
    // Validate output
    const output = build.outputs[0];
    if (!output) throw new Error(`Build ${build.number} has no output line item`);

    // Validate build job
    const buildJob = build.job;
    if (buildJob?.id && buildJob.id !== jobId)
      throw new Error(
        `Build ${build.number} is assigned to job ${buildJob.name}, but the output is assigned to ${jobName}`,
      );

    // Validate output job
    const outputJob = output.job;
    if (outputJob?.id && outputJob.id !== jobId)
      throw new Error(
        `Build ${build.number} is assigned to job ${jobName}, but the output is assigned to job ${outputJob.name}`,
      );

    // Validate input jobs
    build.inputs.forEach((input) => {
      const inputJob = input.job;
      if (inputJob?.id && inputJob.id !== jobId)
        throw new Error(
          `Build ${build.number} input #${input.lineNumber.toFixed()} assigned to job ${inputJob.name}, but the build is assigned to job ${jobName}`,
        );
    });
  });

  // Fail-fast if any of the builds have created using the 'Produce All' button in SOS. This feature in SOS is broken
  // and should not be used. We have gone through the painful process of fixing these once already and do not want to
  // facilitate them in any way.
  const brokenBuilds = queryResult
    .filter((build) => build.inputs.every((x) => x.lineNumber === 0))
    .map((x) => x.number)
    .join(", ");
  if (brokenBuilds.length)
    throw new Error(
      `The following builds have been created using the broken 'Produce All' button in SOS need to be fixed or re-created: ${brokenBuilds}`,
    );

  // Do some additional client-side processing on the results before returning
  return queryResult.map((build) => processBuild(build, CADCurrencyConverter));
}

function processBuild(
  { inputs, outputs, ...build }: BuildSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): Build {
  return {
    ...build,
    inputs: inputs.map((line) => processBuildLineItem(line, CADCurrencyConverter)),
    outputs: outputs.map((line) => processBuildLineItem(line, CADCurrencyConverter)),
    linkedWOs: uniqBy(
      outputs.map((line) => line.linkedWorkOrderLineItem?.workOrder).filter((x) => !!x),
      (x) => x.id,
    ),
  };
}

function processBuildLineItem(
  line: BuildLineItemSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): BuildLineItem {
  return {
    ...line,
    item: processItem(line.item, CADCurrencyConverter),
    baseQuantity: calculateBaseQuantity(line.uomQuantity, line.itemUOM),
    linkedWO: line.linkedWorkOrderLineItem?.workOrder,
  };
}
